import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";

const SUBJECTS = [
  "General", "Anatomy", "Physiology", "Pharmacology",
  "Medical-Surgical", "Pediatric", "Obstetric", "Psychiatric",
  "Community Health", "Other",
];

function slugify(s) {
  return (s || "").toLowerCase().trim().replace(/[^\w]+/g, "-").replace(/(^-|-$)/g, "");
}

export default function Admin() {
  const navigate = useNavigate();
  const [token, setToken] = useState(() => sessionStorage.getItem("ADMIN_TOKEN") || "");

  useEffect(() => {
    sessionStorage.setItem("ADMIN_TOKEN", token || "");
  }, [token]);

  const h = { "x-admin-token": token };

  return (
    <div className="max-w-4xl mx-auto p-6 space-y-16">
      <h1 className="text-3xl font-bold pt-4">Admin Panel</h1>

      {/* Shared token */}
      <section>
        <label className="block text-sm font-semibold text-gray-700 mb-1">Admin Token</label>
        <input
          type="password"
          className="w-full max-w-sm border rounded-lg px-3 py-2"
          placeholder="Enter admin token"
          value={token}
          onChange={(e) => setToken(e.target.value)}
        />
      </section>

      <StudySection token={token} headers={h} navigate={navigate} />
      <MediaSection
        title="Paintings"
        token={token}
        headers={h}
        adminEndpoint="/api/admin/paintings"
        publicBase="/api/paintings"
      />
      <MediaSection
        title="Cat Photos"
        token={token}
        headers={h}
        adminEndpoint="/api/admin/catphotos"
        publicBase="/api/catphotos"
      />
    </div>
  );
}

/* ─── Study Materials section ─── */
function StudySection({ token, headers, navigate }) {
  const [title, setTitle] = useState("");
  const [slug, setSlug] = useState("");
  const [subject, setSubject] = useState("General");
  const [summary, setSummary] = useState("");
  const [cover, setCover] = useState(null);
  const [pdf, setPdf] = useState(null);
  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState("");
  const [err, setErr] = useState("");

  const [posts, setPosts] = useState([]);
  const [listLoading, setListLoading] = useState(false);
  const [listErr, setListErr] = useState("");

  const loadPosts = async () => {
    if (!token) return;
    setListLoading(true);
    setListErr("");
    try {
      const res = await fetch("/api/admin/posts", { headers });
      if (!res.ok) throw new Error(`${res.status}`);
      const data = await res.json();
      setPosts(Array.isArray(data) ? data : data.results || []);
    } catch (e) {
      setListErr(`Failed to load: ${e.message}`);
    } finally {
      setListLoading(false);
    }
  };

  useEffect(() => {
    if (token) loadPosts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const onPublish = async (e) => {
    e.preventDefault();
    setMsg("");
    setErr("");
    if (!token) { setErr("Enter admin token."); return; }
    if (!pdf) { setErr("PDF is required."); return; }

    const fd = new FormData();
    fd.append("title", title);
    fd.append("subject", subject);
    fd.append("summary", summary);
    if (slug) fd.append("slug", slugify(slug));
    fd.append("pdf", pdf);
    if (cover) fd.append("cover", cover);

    setLoading(true);
    try {
      const res = await fetch("/api/admin/posts", { method: "POST", headers, body: fd });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || res.statusText);
      const s = data.slug || slugify(slug || title);
      setMsg(`Published! Opening /study/${s}`);
      await loadPosts();
      navigate(`/study/${s}`);
    } catch (e) {
      setErr(e.message);
    } finally {
      setLoading(false);
    }
  };

  const onDelete = async (s) => {
    if (!window.confirm(`Delete "${s}"? This cannot be undone.`)) return;
    try {
      const res = await fetch(`/api/admin/posts/${encodeURIComponent(s)}`, {
        method: "DELETE",
        headers,
      });
      if (!res.ok) throw new Error(await res.text());
      await loadPosts();
    } catch (e) {
      alert(`Delete failed: ${e.message}`);
    }
  };

  return (
    <section>
      <h2 className="text-2xl font-semibold mb-5 pb-2 border-b">Study Materials</h2>

      <form onSubmit={onPublish} className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <input
            className="border rounded-lg px-3 py-2"
            placeholder="Title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
          />
          <select
            className="border rounded-lg px-3 py-2 bg-white"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
          >
            {SUBJECTS.map((s) => <option key={s}>{s}</option>)}
          </select>
        </div>
        <input
          className="w-full border rounded-lg px-3 py-2"
          placeholder="Custom slug (optional)"
          value={slug}
          onChange={(e) => setSlug(e.target.value)}
        />
        <textarea
          rows={4}
          className="w-full border rounded-lg px-3 py-2"
          placeholder="Summary (shown on listing)"
          value={summary}
          onChange={(e) => setSummary(e.target.value)}
        />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm mb-1 text-gray-600">Cover image (optional)</label>
            <input type="file" accept="image/*" onChange={(e) => setCover(e.target.files[0] || null)} />
          </div>
          <div>
            <label className="block text-sm mb-1 text-gray-600">PDF (required)</label>
            <input type="file" accept="application/pdf" onChange={(e) => setPdf(e.target.files[0] || null)} />
          </div>
        </div>
        <button
          disabled={loading}
          className="px-5 py-2 rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-60 font-medium"
        >
          {loading ? "Publishing…" : "Publish"}
        </button>
        {msg && <p className="text-emerald-700">{msg}</p>}
        {err && <p className="text-red-600">Error: {err}</p>}
      </form>

      {/* Post list */}
      <div className="mt-10">
        <div className="flex items-center gap-3 mb-4">
          <h3 className="text-xl font-semibold">All Posts</h3>
          <button
            onClick={loadPosts}
            disabled={!token || listLoading}
            className="px-3 py-1 rounded border hover:bg-gray-100 disabled:opacity-60 text-sm"
          >
            {listLoading ? "Loading…" : "Refresh"}
          </button>
          {!token && <span className="text-sm text-gray-400">enter token to load</span>}
        </div>
        {listErr && <p className="text-red-600 mb-2">{listErr}</p>}
        {token && posts.length === 0 && !listLoading && (
          <p className="text-gray-500">No posts yet.</p>
        )}
        {posts.length > 0 && (
          <div className="overflow-x-auto border rounded-lg">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="text-left px-3 py-2">Title</th>
                  <th className="text-left px-3 py-2">Subject</th>
                  <th className="text-left px-3 py-2">Slug</th>
                  <th className="text-left px-3 py-2">Published</th>
                  <th className="px-3 py-2">Actions</th>
                </tr>
              </thead>
              <tbody>
                {posts.map((p) => (
                  <tr key={p.slug} className="border-t">
                    <td className="px-3 py-2">{p.title}</td>
                    <td className="px-3 py-2 text-gray-500 text-xs">{p.subject || "General"}</td>
                    <td className="px-3 py-2 font-mono text-xs">{p.slug}</td>
                    <td className="px-3 py-2 text-xs">
                      {p.published_at ? new Date(p.published_at).toLocaleDateString() : "—"}
                    </td>
                    <td className="px-3 py-2 space-x-2 whitespace-nowrap">
                      <Link
                        to={`/study/${p.slug}`}
                        className="px-2 py-1 rounded border hover:bg-gray-100 text-xs"
                      >View</Link>
                      <Link
                        to={`/admin/edit/${p.slug}`}
                        className="px-2 py-1 rounded border hover:bg-gray-100 text-xs"
                      >Edit</Link>
                      <button
                        onClick={() => onDelete(p.slug)}
                        className="px-2 py-1 rounded border hover:bg-red-50 text-red-600 text-xs"
                      >Delete</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </section>
  );
}

/* ─── Generic media upload section (Paintings / Cat Photos) ─── */
function MediaSection({ title, token, headers, adminEndpoint, publicBase }) {
  const [imgTitle, setImgTitle] = useState("");
  const [file, setFile] = useState(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState("");

  const load = async () => {
    if (!token) return;
    try {
      const res = await fetch(adminEndpoint, { headers });
      if (res.ok) setItems(await res.json());
    } catch {}
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const onUpload = async (e) => {
    e.preventDefault();
    if (!file) return;
    const fd = new FormData();
    fd.append("title", imgTitle);
    fd.append("image", file);
    setLoading(true);
    setMsg("");
    try {
      const res = await fetch(adminEndpoint, { method: "POST", headers, body: fd });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Upload failed");
      setMsg("Uploaded!");
      setImgTitle("");
      setFile(null);
      await load();
    } catch (e) {
      setMsg(`Error: ${e.message}`);
    } finally {
      setLoading(false);
    }
  };

  const onDelete = async (id) => {
    if (!window.confirm("Delete this image?")) return;
    await fetch(`${adminEndpoint}/${id}`, { method: "DELETE", headers });
    await load();
  };

  return (
    <section>
      <h2 className="text-2xl font-semibold mb-5 pb-2 border-b">{title}</h2>

      <form onSubmit={onUpload} className="space-y-3">
        <input
          className="w-full max-w-sm border rounded-lg px-3 py-2"
          placeholder="Title (optional)"
          value={imgTitle}
          onChange={(e) => setImgTitle(e.target.value)}
        />
        <div>
          <label className="block text-sm mb-1 text-gray-600">Image (JPG / PNG)</label>
          <input
            type="file"
            accept="image/jpeg,image/png,image/webp"
            onChange={(e) => setFile(e.target.files[0] || null)}
          />
        </div>
        <button
          disabled={!file || loading}
          className="px-5 py-2 rounded-lg bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-60 font-medium"
        >
          {loading ? "Uploading…" : "Upload"}
        </button>
        {msg && (
          <p className={msg.startsWith("Error") ? "text-red-600" : "text-emerald-700"}>
            {msg}
          </p>
        )}
      </form>

      {items.length > 0 && (
        <div className="mt-6 grid grid-cols-2 md:grid-cols-3 gap-4">
          {items.map((item) => (
            <div key={item.id} className="relative group border rounded-xl overflow-hidden">
              <img
                src={`${publicBase}/${item.id}/image`}
                alt={item.title || ""}
                className="w-full h-36 object-cover"
              />
              {item.title && (
                <p className="text-xs text-center py-1 px-2 bg-white text-gray-600 truncate">
                  {item.title}
                </p>
              )}
              <button
                onClick={() => onDelete(item.id)}
                className="absolute top-1 right-1 bg-red-600 text-white rounded-md text-xs px-2 py-0.5
                  opacity-0 group-hover:opacity-100 transition-opacity"
              >
                Delete
              </button>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}
